import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft,
  CreditCard,
  Minus,
  Plus,
  ShoppingCart,
  Star,
  Trash2,
  Utensils,
  X,
  MapPin,
  Phone,
  Mail,
  User,
  Home,
  Calendar,
  Clock,
  CheckCircle,
  Truck,
} from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { TextInput, Select, Textarea } from "@mantine/core";
import { DateInput, TimeInput } from "@mantine/dates";
import { AppName, Packs, Logo } from "../../lib/data";
import { LagosAreas } from "../../lib/areas";

interface CartItem {
  pack: (typeof Packs)[0];
  quantity: number;
}

console.log("ballsacks")

interface CheckoutForm {
  customerInfo: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
  deliveryAddress: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    landmark: string;
  };
  deliveryInfo: {
    preferredLocation: string;
    deliveryDate: string;
    deliveryTime: string;
    specialInstructions: string;
  };
  paymentMethod: "cash" | "card" | "transfer";
}

function Order() {
  const [cart, setCart] = useState<CartItem[]>([]);
  const [showCart, setShowCart] = useState(false);
  const [isCartCollapsed, setIsCartCollapsed] = useState(false);
  const [orderStep, setOrderStep] = useState<
    "menu" | "cart" | "checkout" | "success"
  >("menu");
  const [checkoutForm, setCheckoutForm] = useState<CheckoutForm>({
    customerInfo: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
    },
    deliveryAddress: {
      street: "",
      city: "",
      state: "",
      zipCode: "",
      landmark: "",
    },
    deliveryInfo: {
      preferredLocation: "",
      deliveryDate: "",
      deliveryTime: "",
      specialInstructions: "",
    },
    paymentMethod: "cash",
  });
  const [checkoutView, setCheckoutView] = useState<"cart" | "summary">(
    "summary"
  );

  const addToCart = (pack: (typeof Packs)[0]) => {
    setCart((prevCart) => {
      const existingItem = prevCart.find((item) => item.pack.id === pack.id);
      if (existingItem) {
        return prevCart.map((item) =>
          item.pack.id === pack.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prevCart, { pack, quantity: 1 }];
    });
  };

  const removeFromCart = (packId: string) => {
    setCart((prevCart) => prevCart.filter((item) => item.pack.id !== packId));
  };

  const updateQuantity = (packId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(packId);
      return;
    }
    setCart((prevCart) =>
      prevCart.map((item) =>
        item.pack.id === packId ? { ...item, quantity } : item
      )
    );
  };

  const getTotalPrice = () => {
    return cart.reduce(
      (total, item) => total + item.pack.price * item.quantity,
      0
    );
  };

  const getTotalItems = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  const handleCheckoutFormChange = (
    section: keyof CheckoutForm,
    field: string,
    value: string
  ) => {
    setCheckoutForm((prev) => {
      const newForm = { ...prev };
      if (section === "customerInfo") {
        newForm.customerInfo = { ...prev.customerInfo, [field]: value };
      } else if (section === "deliveryAddress") {
        newForm.deliveryAddress = { ...prev.deliveryAddress, [field]: value };
      } else if (section === "deliveryInfo") {
        newForm.deliveryInfo = { ...prev.deliveryInfo, [field]: value };
      } else if (section === "paymentMethod") {
        newForm.paymentMethod = value as "cash" | "card" | "transfer";
      }
      return newForm;
    });
  };

  const handleOrderSubmit = () => {
    // Here you would typically send the order to your backend
    console.log("Order submitted:", { cart, checkoutForm });
    setOrderStep("success");
  };

  const getDeliveryFee = () => {
    return 1000; // Fixed delivery fee
  };

  const getTotalWithDelivery = () => {
    return getTotalPrice() + getDeliveryFee();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-red-50 to-yellow-50">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/90 backdrop-blur-md z-50 border-b border-red-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-3">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="flex items-center space-x-3"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-red-600 to-red-800 rounded-lg flex items-center justify-center">
                  <img src={Logo} alt={AppName} className="w-8 h-8 rounded" />
                </div>
                <span className="text-xl font-bold text-gray-800">
                  {AppName}
                </span>
              </motion.div>
            </Link>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center space-x-4"
            >
              <button
                onClick={() => setShowCart(!showCart)}
                className="relative bg-gradient-to-r from-red-600 via-orange-500 to-yellow-500 text-white p-3 rounded-full hover:from-red-700 hover:via-orange-600 hover:to-yellow-600 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <ShoppingCart className="w-5 h-5" />
                {getTotalItems() > 0 && (
                  <span className="absolute -top-2 -right-2 bg-white text-red-600 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">
                    {getTotalItems()}
                  </span>
                )}
              </button>
            </motion.div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="pt-20 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <div className="flex items-center justify-center mb-4">
              <Link
                to="/"
                className="flex items-center space-x-2 text-gray-600 hover:text-red-600 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Back to Home</span>
              </Link>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
              Place Your{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 via-orange-500 to-yellow-500">
                Order
              </span>
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Choose from our delicious menu packs and customize your order
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Menu Section */}
            {orderStep === "menu" && (
              <div className="lg:col-span-2 lg:mr-96">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="grid md:grid-cols-2 gap-6"
                >
                  {Packs.map((pack, index) => (
                    <motion.div
                      key={pack.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-orange-100 overflow-hidden flex flex-col"
                    >
                      {pack.name === "Premium Pack" && (
                        <div className="bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 text-white px-4 py-2 text-center text-sm font-semibold flex items-center justify-center space-x-2">
                          <Star className="w-4 h-4" />
                          <span>Most Popular</span>
                        </div>
                      )}

                      <div className="p-6 flex flex-col flex-1">
                        <div className="text-center mb-4">
                          <h3 className="text-xl font-bold text-gray-800 mb-2">
                            {pack.name}
                          </h3>
                          <div className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-600 via-orange-500 to-yellow-500 mb-3">
                            ₦{pack.price.toLocaleString()}
                          </div>
                          <div className="w-12 h-1 bg-gradient-to-r from-red-600 via-orange-500 to-yellow-500 mx-auto rounded-full"></div>
                        </div>

                        <div className="space-y-2 mb-6 flex-1">
                          {pack.items.map((item, itemIndex) => (
                            <div
                              key={itemIndex}
                              className="flex items-center justify-between py-1 text-sm"
                            >
                              <span className="text-gray-700">{item.name}</span>
                              <span className="text-red-600 font-semibold">
                                {item.count}
                              </span>
                            </div>
                          ))}
                        </div>

                        {/* Cart Actions */}
                        {(() => {
                          const cartItem = cart.find(
                            (item) => item.pack.id === pack.id
                          );
                          return cartItem ? (
                            <div className="mt-auto">
                              <div className="flex items-center justify-between mb-3">
                                <span className="text-sm font-medium text-gray-600">
                                  In Cart: {cartItem.quantity}
                                </span>
                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={() => addToCart(pack)}
                                    className="w-8 h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center hover:bg-green-200 transition-colors"
                                    title="Add one more"
                                  >
                                    <Plus className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => removeFromCart(pack.id)}
                                    className="w-8 h-8 bg-red-100 text-red-600 rounded-full flex items-center justify-center hover:bg-red-200 transition-colors"
                                    title="Remove all"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                              <div className="flex items-center justify-center space-x-3">
                                <button
                                  onClick={() =>
                                    updateQuantity(
                                      pack.id,
                                      cartItem.quantity - 1
                                    )
                                  }
                                  className="w-10 h-10 bg-red-100 text-red-600 rounded-full flex items-center justify-center hover:bg-red-200 transition-colors"
                                  title="Remove one"
                                >
                                  <Minus className="w-5 h-5" />
                                </button>
                                <span className="font-bold text-gray-800 text-lg min-w-[2rem] text-center">
                                  {cartItem.quantity}
                                </span>
                                <button
                                  onClick={() =>
                                    updateQuantity(
                                      pack.id,
                                      cartItem.quantity + 1
                                    )
                                  }
                                  className="w-10 h-10 bg-green-100 text-green-600 rounded-full flex items-center justify-center hover:bg-green-200 transition-colors"
                                  title="Add one more"
                                >
                                  <Plus className="w-5 h-5" />
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button
                              onClick={() => addToCart(pack)}
                              className="w-full bg-gradient-to-r from-red-600 via-orange-500 to-yellow-500 text-white py-3 px-4 rounded-full font-semibold hover:from-red-700 hover:via-orange-600 hover:to-yellow-600 transition-all duration-300 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl transform hover:scale-105 mt-auto"
                            >
                              <Plus className="w-5 h-5" />
                              <span>Add to Cart</span>
                            </button>
                          );
                        })()}
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              </div>
            )}

            {/* Checkout Form */}
            {orderStep === "checkout" && (
              <div className="lg:col-span-2 lg:mr-96">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-2xl shadow-xl border border-orange-100 p-8"
                >
                  {/* Header */}
                  <div className="flex items-center justify-between mb-8">
                    <div>
                      <h2 className="text-3xl font-bold text-gray-800 mb-2">
                        Complete Your Order
                      </h2>
                      <p className="text-gray-600">
                        Fill in your details to complete your purchase
                      </p>
                    </div>
                    <button
                      onClick={() => setOrderStep("menu")}
                      className="flex items-center space-x-2 text-gray-600 hover:text-red-600 transition-colors"
                    >
                      <ArrowLeft className="w-5 h-5" />
                      <span>Back to Menu</span>
                    </button>
                  </div>

                  <div className="space-y-8">
                    {/* Form Sections */}
                    <div className="space-y-8">
                      {/* Customer Information */}
                      <div className="bg-gradient-to-br from-orange-50 via-red-50 to-yellow-50 rounded-xl p-6 border border-orange-100">
                        <div className="flex items-center space-x-3 mb-6">
                          <div className="w-10 h-10 bg-gradient-to-br from-red-600 via-orange-500 to-yellow-500 rounded-lg flex items-center justify-center">
                            <User className="w-5 h-5 text-white" />
                          </div>
                          <h3 className="text-xl font-bold text-gray-800">
                            Customer Information
                          </h3>
                        </div>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              First Name
                            </label>
                            <TextInput
                              value={checkoutForm.customerInfo.firstName}
                              onChange={(event) =>
                                handleCheckoutFormChange(
                                  "customerInfo",
                                  "firstName",
                                  event.currentTarget.value
                                )
                              }
                              className="w-full"
                              placeholder="Enter your first name"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Last Name
                            </label>
                            <TextInput
                              value={checkoutForm.customerInfo.lastName}
                              onChange={(event) =>
                                handleCheckoutFormChange(
                                  "customerInfo",
                                  "lastName",
                                  event.currentTarget.value
                                )
                              }
                              className="w-full"
                              placeholder="Enter your last name"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Email Address
                            </label>
                            <TextInput
                              value={checkoutForm.customerInfo.email}
                              onChange={(event) =>
                                handleCheckoutFormChange(
                                  "customerInfo",
                                  "email",
                                  event.currentTarget.value
                                )
                              }
                              className="w-full"
                              placeholder="Enter your email"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Phone Number
                            </label>
                            <TextInput
                              value={checkoutForm.customerInfo.phone}
                              onChange={(event) =>
                                handleCheckoutFormChange(
                                  "customerInfo",
                                  "phone",
                                  event.currentTarget.value
                                )
                              }
                              className="w-full"
                              placeholder="Enter your phone number"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Delivery Address */}
                      <div className="bg-gradient-to-br from-orange-50 via-red-50 to-yellow-50 rounded-xl p-6 border border-orange-100">
                        <div className="flex items-center space-x-3 mb-6">
                          <div className="w-10 h-10 bg-gradient-to-br from-red-600 via-orange-500 to-yellow-500 rounded-lg flex items-center justify-center">
                            <Home className="w-5 h-5 text-white" />
                          </div>
                          <h3 className="text-xl font-bold text-gray-800">
                            Delivery Address
                          </h3>
                        </div>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Street Address
                            </label>
                            <TextInput
                              value={checkoutForm.deliveryAddress.street}
                              onChange={(event) =>
                                handleCheckoutFormChange(
                                  "deliveryAddress",
                                  "street",
                                  event.currentTarget.value
                                )
                              }
                              className="w-full"
                              placeholder="Enter your street address"
                            />
                          </div>
                          <div className="grid md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                City
                              </label>
                              <Select
                                value={checkoutForm.deliveryAddress.city}
                                onChange={(value) =>
                                  handleCheckoutFormChange(
                                    "deliveryAddress",
                                    "city",
                                    value || ""
                                  )
                                }
                                className="w-full"
                                placeholder="Select a city"
                                data={LagosAreas.map((area) => ({
                                  value: area,
                                  label: area,
                                }))}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                ZIP Code
                              </label>
                              <TextInput
                                value={checkoutForm.deliveryAddress.zipCode}
                                onChange={(event) =>
                                  handleCheckoutFormChange(
                                    "deliveryAddress",
                                    "zipCode",
                                    event.currentTarget.value
                                  )
                                }
                                className="w-full"
                                placeholder="ZIP Code"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Landmark (Optional)
                            </label>
                            <TextInput
                              value={checkoutForm.deliveryAddress.landmark}
                              onChange={(event) =>
                                handleCheckoutFormChange(
                                  "deliveryAddress",
                                  "landmark",
                                  event.currentTarget.value
                                )
                              }
                              className="w-full"
                              placeholder="Nearby landmark or building"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Delivery Information */}
                      <div className="bg-gradient-to-br from-orange-50 via-red-50 to-yellow-50 rounded-xl p-6 border border-orange-100">
                        <div className="flex items-center space-x-3 mb-6">
                          <div className="w-10 h-10 bg-gradient-to-br from-red-600 via-orange-500 to-yellow-500 rounded-lg flex items-center justify-center">
                            <Truck className="w-5 h-5 text-white" />
                          </div>
                          <h3 className="text-xl font-bold text-gray-800">
                            Delivery Information
                          </h3>
                        </div>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Preferred Location
                            </label>
                            <Select
                              value={
                                checkoutForm.deliveryInfo.preferredLocation
                              }
                              onChange={(value) =>
                                handleCheckoutFormChange(
                                  "deliveryInfo",
                                  "preferredLocation",
                                  value || ""
                                )
                              }
                              className="w-full"
                              placeholder="Select a location"
                              data={[
                                { value: "", label: "Select a location" },
                                { value: "admiralty", label: "Admiralty" },
                                { value: "opebi", label: "Opebi" },
                                { value: "ikoyi", label: "Ikoyi" },
                                { value: "ikota", label: "Ikota" },
                                { value: "osapa", label: "Osapa" },
                              ]}
                            />
                          </div>
                          <div className="grid md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Delivery Date
                              </label>
                              <DateInput
                                value={checkoutForm.deliveryInfo.deliveryDate}
                                onChange={(value) =>
                                  handleCheckoutFormChange(
                                    "deliveryInfo",
                                    "deliveryDate",
                                    value || ""
                                  )
                                }
                                className="w-full"
                                placeholder="Select delivery date"
                                valueFormat="YYYY-MM-DD"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Delivery Time
                              </label>
                              <TimeInput
                                value={checkoutForm.deliveryInfo.deliveryTime}
                                onChange={(event) =>
                                  handleCheckoutFormChange(
                                    "deliveryInfo",
                                    "deliveryTime",
                                    event.currentTarget.value
                                  )
                                }
                                className="w-full"
                                placeholder="Select delivery time"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Special Instructions
                            </label>
                            <Textarea
                              value={
                                checkoutForm.deliveryInfo.specialInstructions
                              }
                              onChange={(event) =>
                                handleCheckoutFormChange(
                                  "deliveryInfo",
                                  "specialInstructions",
                                  event.currentTarget.value
                                )
                              }
                              className="w-full"
                              placeholder="Any special delivery instructions..."
                            />
                          </div>
                        </div>
                      </div>

                      {/* Payment Method */}
                      <div className="bg-gradient-to-br from-orange-50 via-red-50 to-yellow-50 rounded-xl p-6 border border-orange-100">
                        <div className="flex items-center space-x-3 mb-6">
                          <div className="w-10 h-10 bg-gradient-to-br from-red-600 via-orange-500 to-yellow-500 rounded-lg flex items-center justify-center">
                            <CreditCard className="w-5 h-5 text-white" />
                          </div>
                          <h3 className="text-xl font-bold text-gray-800">
                            Payment Method
                          </h3>
                        </div>
                        <div className="space-y-3">
                          <label className="flex items-center space-x-3 p-4 border border-gray-300 rounded-lg hover:border-red-500 transition-colors cursor-pointer">
                            <input
                              type="radio"
                              name="paymentMethod"
                              value="cash"
                              checked={checkoutForm.paymentMethod === "cash"}
                              onChange={(event) =>
                                handleCheckoutFormChange(
                                  "paymentMethod",
                                  "paymentMethod",
                                  event.target.value
                                )
                              }
                              className="w-4 h-4 text-red-600 focus:ring-red-500"
                            />
                            <div>
                              <div className="font-medium text-gray-800">
                                Cash on Delivery
                              </div>
                              <div className="text-sm text-gray-600">
                                Pay when your order arrives
                              </div>
                            </div>
                          </label>
                          <label className="flex items-center space-x-3 p-4 border border-gray-300 rounded-lg hover:border-red-500 transition-colors cursor-pointer">
                            <input
                              type="radio"
                              name="paymentMethod"
                              value="transfer"
                              checked={
                                checkoutForm.paymentMethod === "transfer"
                              }
                              onChange={(event) =>
                                handleCheckoutFormChange(
                                  "paymentMethod",
                                  "paymentMethod",
                                  event.target.value
                                )
                              }
                              className="w-4 h-4 text-red-600 focus:ring-red-500"
                            />
                            <div>
                              <div className="font-medium text-gray-800">
                                Bank Transfer
                              </div>
                              <div className="text-sm text-gray-600">
                                Transfer to our account before delivery
                              </div>
                            </div>
                          </label>
                          <label className="flex items-center space-x-3 p-4 border border-gray-300 rounded-lg hover:border-red-500 transition-colors cursor-pointer">
                            <input
                              type="radio"
                              name="paymentMethod"
                              value="card"
                              checked={checkoutForm.paymentMethod === "card"}
                              onChange={(event) =>
                                handleCheckoutFormChange(
                                  "paymentMethod",
                                  "paymentMethod",
                                  event.target.value
                                )
                              }
                              className="w-4 h-4 text-red-600 focus:ring-red-500"
                            />
                            <div>
                              <div className="font-medium text-gray-800">
                                Card Payment
                              </div>
                              <div className="text-sm text-gray-600">
                                Pay securely with your card
                              </div>
                            </div>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            )}

            {/* Order Summary - Fixed Right Side */}
            {orderStep === "checkout" && (
              <div className="hidden lg:block fixed top-32 right-6 w-96 z-40">
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="bg-white rounded-2xl shadow-xl border border-orange-100 p-6 max-h-[calc(100vh-8rem)] overflow-y-auto"
                >
                  {/* Toggle Header */}
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold text-gray-800">
                      {checkoutView === "cart" ? "Your Cart" : "Order Summary"}
                    </h3>
                    <div className="flex bg-gray-100 rounded-lg p-1">
                      <button
                        onClick={() => setCheckoutView("cart")}
                        className={`px-3 py-1 rounded-md text-sm font-medium transition-all ${
                          checkoutView === "cart"
                            ? "bg-white text-red-600 shadow-sm"
                            : "text-gray-600 hover:text-gray-800"
                        }`}
                      >
                        Cart
                      </button>
                      <button
                        onClick={() => setCheckoutView("summary")}
                        className={`px-3 py-1 rounded-md text-sm font-medium transition-all ${
                          checkoutView === "summary"
                            ? "bg-white text-red-600 shadow-sm"
                            : "text-gray-600 hover:text-gray-800"
                        }`}
                      >
                        Summary
                      </button>
                    </div>
                  </div>

                  {/* Cart View */}
                  {checkoutView === "cart" && (
                    <>
                      <div className="space-y-4 mb-6 max-h-96 overflow-y-auto">
                        {cart.map((item) => (
                          <div
                            key={item.pack.id}
                            className="bg-gray-50 rounded-lg p-4 border border-gray-200"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <h3 className="font-semibold text-gray-800">
                                {item.pack.name}
                              </h3>
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => addToCart(item.pack)}
                                  className="w-8 h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center hover:bg-green-200 transition-colors"
                                  title="Add one more"
                                >
                                  <Plus className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => removeFromCart(item.pack.id)}
                                  className="text-red-500 hover:text-red-700 transition-colors"
                                  title="Remove all"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() =>
                                    updateQuantity(
                                      item.pack.id,
                                      item.quantity - 1
                                    )
                                  }
                                  className="w-8 h-8 bg-red-100 text-red-600 rounded-full flex items-center justify-center hover:bg-red-200 transition-colors"
                                  title="Remove one"
                                >
                                  <Minus className="w-4 h-4" />
                                </button>
                                <span className="font-semibold text-gray-800 w-8 text-center">
                                  {item.quantity}
                                </span>
                                <button
                                  onClick={() =>
                                    updateQuantity(
                                      item.pack.id,
                                      item.quantity + 1
                                    )
                                  }
                                  className="w-8 h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center hover:bg-green-200 transition-colors"
                                  title="Add one more"
                                >
                                  <Plus className="w-4 h-4" />
                                </button>
                              </div>
                              <span className="font-bold text-red-600">
                                ₦
                                {(
                                  item.pack.price * item.quantity
                                ).toLocaleString()}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="border-t border-gray-200 pt-4">
                        <div className="flex justify-between items-center mb-4">
                          <span className="text-lg font-semibold text-gray-800">
                            Total:
                          </span>
                          <span className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-600 via-orange-500 to-yellow-500">
                            ₦{getTotalPrice().toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Order Summary View */}
                  {checkoutView === "summary" && (
                    <>
                      <div className="space-y-4 mb-6">
                        {cart.map((item) => (
                          <div
                            key={item.pack.id}
                            className="flex items-center justify-between py-2 border-b border-orange-100 last:border-b-0"
                          >
                            <div>
                              <div className="font-medium text-gray-800">
                                {item.pack.name}
                              </div>
                              <div className="text-sm text-gray-600">
                                Quantity: {item.quantity}
                              </div>
                            </div>
                            <div className="font-bold text-red-600">
                              ₦
                              {(
                                item.pack.price * item.quantity
                              ).toLocaleString()}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="space-y-3 border-t border-orange-100 pt-4">
                        <div className="flex justify-between text-gray-600">
                          <span>Subtotal:</span>
                          <span>₦{getTotalPrice().toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between text-gray-600">
                          <span>Delivery Fee:</span>
                          <span>₦{getDeliveryFee().toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between text-lg font-bold text-gray-800 border-t border-orange-100 pt-3">
                          <span>Total:</span>
                          <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 via-orange-500 to-yellow-500">
                            ₦{getTotalWithDelivery().toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Place Order Button - Always Visible */}
                  <button
                    onClick={handleOrderSubmit}
                    className="w-full bg-gradient-to-r from-red-600 via-orange-500 to-yellow-500 text-white py-4 px-6 rounded-full font-semibold hover:from-red-700 hover:via-orange-600 hover:to-yellow-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center justify-center space-x-2 mt-6"
                  >
                    <CheckCircle className="w-5 h-5" />
                    <span>Place Order</span>
                  </button>
                </motion.div>
              </div>
            )}

            {/* Success Screen */}
            {orderStep === "success" && (
              <div className="lg:col-span-2 lg:mr-96">
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-white rounded-2xl shadow-xl border border-orange-100 p-12 text-center"
                >
                  <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-10 h-10 text-white" />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-800 mb-4">
                    Order Placed Successfully!
                  </h2>
                  <p className="text-gray-600 mb-8 max-w-md mx-auto">
                    Thank you for your order! We've received your request and
                    will contact you shortly to confirm the details and arrange
                    delivery.
                  </p>
                  <div className="space-y-4">
                    <Link
                      to="/"
                      className="inline-block bg-gradient-to-r from-red-600 via-orange-500 to-yellow-500 text-white px-8 py-3 rounded-full font-semibold hover:from-red-700 hover:via-orange-600 hover:to-yellow-600 transition-all duration-300 shadow-lg hover:shadow-xl"
                    >
                      Back to Home
                    </Link>
                    <button
                      onClick={() => {
                        setCart([]);
                        setOrderStep("menu");
                      }}
                      className="block mx-auto text-gray-600 hover:text-red-600 transition-colors"
                    >
                      Place Another Order
                    </button>
                  </div>
                </motion.div>
              </div>
            )}
          </div>

          {/* Cart Section - Desktop Fixed Right */}
          {orderStep !== "checkout" && (
            <div className="hidden lg:block fixed top-32 right-6 w-96 z-40">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-white rounded-2xl shadow-xl border border-orange-100 p-6 max-h-[calc(100vh-8rem)] overflow-y-auto"
              >
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-800">
                    Your Cart
                  </h2>
                  <ShoppingCart className="w-6 h-6 text-red-600" />
                </div>

                {cart.length === 0 ? (
                  <div className="text-center py-8">
                    <ShoppingCart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">Your cart is empty</p>
                    <p className="text-sm text-gray-400">
                      Add some delicious items to get started!
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="space-y-4 mb-6 max-h-96 overflow-y-auto">
                      {cart.map((item) => (
                        <div
                          key={item.pack.id}
                          className="bg-gray-50 rounded-lg p-4 border border-gray-200"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="font-semibold text-gray-800">
                              {item.pack.name}
                            </h3>
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => addToCart(item.pack)}
                                className="w-8 h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center hover:bg-green-200 transition-colors"
                                title="Add one more"
                              >
                                <Plus className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => removeFromCart(item.pack.id)}
                                className="text-red-500 hover:text-red-700 transition-colors"
                                title="Remove all"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() =>
                                  updateQuantity(
                                    item.pack.id,
                                    item.quantity - 1
                                  )
                                }
                                className="w-8 h-8 bg-red-100 text-red-600 rounded-full flex items-center justify-center hover:bg-red-200 transition-colors"
                                title="Remove one"
                              >
                                <Minus className="w-4 h-4" />
                              </button>
                              <span className="font-semibold text-gray-800 w-8 text-center">
                                {item.quantity}
                              </span>
                              <button
                                onClick={() =>
                                  updateQuantity(
                                    item.pack.id,
                                    item.quantity + 1
                                  )
                                }
                                className="w-8 h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center hover:bg-green-200 transition-colors"
                                title="Add one more"
                              >
                                <Plus className="w-4 h-4" />
                              </button>
                            </div>
                            <span className="font-bold text-red-600">
                              ₦
                              {(
                                item.pack.price * item.quantity
                              ).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="border-t border-gray-200 pt-4">
                      <div className="flex justify-between items-center mb-4">
                        <span className="text-lg font-semibold text-gray-800">
                          Total:
                        </span>
                        <span className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-600 via-orange-500 to-yellow-500">
                          ₦{getTotalPrice().toLocaleString()}
                        </span>
                      </div>

                      <button
                        onClick={() => setOrderStep("checkout")}
                        className="w-full bg-gradient-to-r from-red-600 via-orange-500 to-yellow-500 text-white py-4 px-6 rounded-full font-semibold hover:from-red-700 hover:via-orange-600 hover:to-yellow-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center justify-center space-x-2"
                      >
                        <CreditCard className="w-5 h-5" />
                        <span>Proceed to Checkout</span>
                      </button>
                    </div>
                  </>
                )}
              </motion.div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Cart Overlay */}
      <AnimatePresence>
        {showCart && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 lg:hidden"
            onClick={() => setShowCart(false)}
          >
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-2xl max-h-[80vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-800">
                    Your Cart ({getTotalItems()})
                  </h2>
                  <button
                    onClick={() => setShowCart(false)}
                    className="text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                {cart.length === 0 ? (
                  <div className="text-center py-8">
                    <ShoppingCart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">Your cart is empty</p>
                    <p className="text-sm text-gray-400">
                      Add some delicious items to get started!
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="space-y-4 mb-6 max-h-96 overflow-y-auto">
                      {cart.map((item) => (
                        <div
                          key={item.pack.id}
                          className="bg-gray-50 rounded-lg p-4 border border-gray-200"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="font-semibold text-gray-800">
                              {item.pack.name}
                            </h3>
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => addToCart(item.pack)}
                                className="w-8 h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center hover:bg-green-200 transition-colors"
                                title="Add one more"
                              >
                                <Plus className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => removeFromCart(item.pack.id)}
                                className="text-red-500 hover:text-red-700 transition-colors"
                                title="Remove all"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() =>
                                  updateQuantity(
                                    item.pack.id,
                                    item.quantity - 1
                                  )
                                }
                                className="w-8 h-8 bg-red-100 text-red-600 rounded-full flex items-center justify-center hover:bg-red-200 transition-colors"
                                title="Remove one"
                              >
                                <Minus className="w-4 h-4" />
                              </button>
                              <span className="font-semibold text-gray-800 w-8 text-center">
                                {item.quantity}
                              </span>
                              <button
                                onClick={() =>
                                  updateQuantity(
                                    item.pack.id,
                                    item.quantity + 1
                                  )
                                }
                                className="w-8 h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center hover:bg-green-200 transition-colors"
                                title="Add one more"
                              >
                                <Plus className="w-4 h-4" />
                              </button>
                            </div>
                            <span className="font-bold text-red-600">
                              ₦
                              {(
                                item.pack.price * item.quantity
                              ).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="border-t border-gray-200 pt-4">
                      <div className="flex justify-between items-center mb-4">
                        <span className="text-lg font-semibold text-gray-800">
                          Total:
                        </span>
                        <span className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-600 via-orange-500 to-yellow-500">
                          ₦{getTotalPrice().toLocaleString()}
                        </span>
                      </div>

                      <button
                        onClick={() => setOrderStep("checkout")}
                        className="w-full bg-gradient-to-r from-red-600 via-orange-500 to-yellow-500 text-white py-4 px-6 rounded-full font-semibold hover:from-red-700 hover:via-orange-600 hover:to-yellow-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center justify-center space-x-2"
                      >
                        <CreditCard className="w-5 h-5" />
                        <span>Proceed to Checkout</span>
                      </button>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Action Bar - Mobile */}
      {getTotalItems() > 0 && (
        <motion.div
          initial={{ y: 100 }}
          animate={{ y: 0 }}
          className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-2xl z-40 lg:hidden"
        >
          <div className="px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <ShoppingCart className="w-6 h-6 text-red-600" />
                  <span className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">
                    {getTotalItems()}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Items</p>
                  <p className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-600 via-orange-500 to-yellow-500">
                    ₦{getTotalPrice().toLocaleString()}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setShowCart(true)}
                  className="bg-gray-100 text-gray-700 px-4 py-2 rounded-full text-sm font-semibold hover:bg-gray-200 transition-colors"
                >
                  View Cart
                </button>
                <button
                  onClick={() => setOrderStep("checkout")}
                  className="bg-gradient-to-r from-red-600 via-orange-500 to-yellow-500 text-white px-6 py-2 rounded-full text-sm font-semibold hover:from-red-700 hover:via-orange-600 hover:to-yellow-600 transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  Checkout
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}

export default Order;
